import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  BarChart3,
  CalendarDays,
  Compass,
  FileText,
  GitBranch,
  Github,
  Kanban,
  Layers,
  ListTodo,
  Menu,
  MoreHorizontal,
  Settings,
  Star,
  Trash2,
  Users,
  Zap,
  User,
} from 'lucide-react'
import { deleteSpace, type SpaceDetail } from '../api'
import { useAuth } from '../../../context/AuthContext'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'

export interface SpaceHeaderProps {
  space: SpaceDetail | undefined
  onOpenMembers: () => void
  actions?: React.ReactNode
  assignedCount?: number
  onOpenMobileNav?: () => void
}

interface TabTheme {
  iconColor: string
  iconBg: string
  activeIconColor: string
  activeIconBg: string
  activeText: string
  activeBg: string
  activeIndicator: string
}

interface SpaceTab {
  label: string
  suffix: string
  icon: React.ComponentType<{ className?: string }>
  theme: TabTheme
}

const TABS: SpaceTab[] = [
  {
    label: 'Summary',
    suffix: '/summary',
    icon: Compass,
    theme: {
      iconColor: 'text-blue-600 dark:text-blue-400',
      iconBg: 'bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-blue-500/30',
      activeText: 'text-blue-700 dark:text-blue-300',
      activeBg: 'bg-blue-50/80 dark:bg-blue-950/40',
      activeIndicator: 'bg-gradient-to-r from-blue-600 to-indigo-600',
    },
  },
  {
    label: 'List',
    suffix: '/list',
    icon: ListTodo,
    theme: {
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      iconBg: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-emerald-500/30',
      activeText: 'text-emerald-700 dark:text-emerald-300',
      activeBg: 'bg-emerald-50/80 dark:bg-emerald-950/40',
      activeIndicator: 'bg-gradient-to-r from-emerald-500 to-teal-600',
    },
  },
  {
    label: 'Board',
    suffix: '/board',
    icon: Kanban,
    theme: {
      iconColor: 'text-violet-600 dark:text-violet-400',
      iconBg: 'bg-violet-50 text-violet-600 dark:bg-violet-950/60 dark:text-violet-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-violet-500/30',
      activeText: 'text-violet-700 dark:text-violet-300',
      activeBg: 'bg-violet-50/80 dark:bg-violet-950/40',
      activeIndicator: 'bg-gradient-to-r from-violet-500 to-purple-600',
    },
  },
  {
    label: 'Backlog',
    suffix: '/backlog',
    icon: Layers,
    theme: {
      iconColor: 'text-amber-600 dark:text-amber-400',
      iconBg: 'bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-amber-500/30',
      activeText: 'text-amber-800 dark:text-amber-300',
      activeBg: 'bg-amber-50/80 dark:bg-amber-950/40',
      activeIndicator: 'bg-gradient-to-r from-amber-500 to-orange-500',
    },
  },
  {
    label: 'Calendar',
    suffix: '/calendar',
    icon: CalendarDays,
    theme: {
      iconColor: 'text-rose-600 dark:text-rose-400',
      iconBg: 'bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-rose-500/30',
      activeText: 'text-rose-700 dark:text-rose-300',
      activeBg: 'bg-rose-50/80 dark:bg-rose-950/40',
      activeIndicator: 'bg-gradient-to-r from-rose-500 to-pink-600',
    },
  },
  {
    label: 'Timeline',
    suffix: '/timeline',
    icon: GitBranch,
    theme: {
      iconColor: 'text-cyan-600 dark:text-cyan-400',
      iconBg: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950/60 dark:text-cyan-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-cyan-500 to-sky-600 text-white shadow-cyan-500/30',
      activeText: 'text-cyan-800 dark:text-cyan-300',
      activeBg: 'bg-cyan-50/80 dark:bg-cyan-950/40',
      activeIndicator: 'bg-gradient-to-r from-cyan-500 to-sky-600',
    },
  },
  {
    label: 'Reports',
    suffix: '/reports',
    icon: BarChart3,
    theme: {
      iconColor: 'text-indigo-600 dark:text-indigo-400',
      iconBg: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-indigo-500 to-blue-600 text-white shadow-indigo-500/30',
      activeText: 'text-indigo-700 dark:text-indigo-300',
      activeBg: 'bg-indigo-50/80 dark:bg-indigo-950/40',
      activeIndicator: 'bg-gradient-to-r from-indigo-500 to-blue-600',
    },
  },
  {
    label: 'Docs',
    suffix: '/docs',
    icon: FileText,
    theme: {
      iconColor: 'text-fuchsia-600 dark:text-fuchsia-400',
      iconBg: 'bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-950/60 dark:text-fuchsia-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white shadow-fuchsia-500/30',
      activeText: 'text-fuchsia-700 dark:text-fuchsia-300',
      activeBg: 'bg-fuchsia-50/80 dark:bg-fuchsia-950/40',
      activeIndicator: 'bg-gradient-to-r from-fuchsia-500 to-pink-600',
    },
  },
  {
    label: 'Automation',
    suffix: '/automation',
    icon: Zap,
    theme: {
      iconColor: 'text-amber-500 dark:text-amber-400',
      iconBg: 'bg-amber-50 text-amber-500 dark:bg-amber-950/60 dark:text-amber-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-amber-500/30',
      activeText: 'text-amber-800 dark:text-amber-300',
      activeBg: 'bg-amber-50/80 dark:bg-amber-950/40',
      activeIndicator: 'bg-gradient-to-r from-amber-400 to-orange-500',
    },
  },
  {
    label: 'GitHub',
    suffix: '/github',
    icon: Github,
    theme: {
      iconColor: 'text-slate-700 dark:text-slate-300',
      iconBg: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-slate-700 to-slate-900 text-white dark:from-slate-600 dark:to-slate-800 shadow-slate-500/30',
      activeText: 'text-slate-900 dark:text-slate-100',
      activeBg: 'bg-slate-100/90 dark:bg-slate-800/50',
      activeIndicator: 'bg-gradient-to-r from-slate-700 to-slate-900 dark:from-slate-300 dark:to-slate-100',
    },
  },
  {
    label: 'Project Progress',
    suffix: '/employee-dashboard',
    icon: User,
    theme: {
      iconColor: 'text-orange-600 dark:text-orange-400',
      iconBg: 'bg-orange-50 text-orange-600 dark:bg-orange-950/60 dark:text-orange-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-orange-500 to-rose-600 text-white shadow-orange-500/30',
      activeText: 'text-orange-700 dark:text-orange-300',
      activeBg: 'bg-orange-50/80 dark:bg-orange-950/40',
      activeIndicator: 'bg-gradient-to-r from-orange-500 to-rose-600',
    },
  },
  {
    label: 'Settings',
    suffix: '/settings',
    icon: Settings,
    theme: {
      iconColor: 'text-slate-600 dark:text-slate-400',
      iconBg: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
      activeIconColor: 'text-white',
      activeIconBg: 'bg-gradient-to-br from-slate-600 to-slate-800 text-white shadow-slate-500/30',
      activeText: 'text-slate-900 dark:text-slate-100',
      activeBg: 'bg-slate-100/90 dark:bg-slate-800/50',
      activeIndicator: 'bg-gradient-to-r from-slate-500 to-slate-700',
    },
  },
]

function cn(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ')
}

export function SpaceHeader({ space, onOpenMembers, actions, assignedCount, onOpenMobileNav }: SpaceHeaderProps) {
  const navigate = useNavigate()
  const { id: routeId } = useParams<{ id: string }>()
  const spaceId = space?.id || routeId || ''
  const { user } = useAuth()

  const isSuperAdmin = Boolean(
    (user?.role && isSuperAdminOrOwner(user.role)) ||
    user?.department?.toLowerCase() === 'superadmin'
  )

  const visibleTabs = TABS.filter((tab) => {
    if (tab.suffix === '/settings' && !isSuperAdmin) {
      return false
    }
    return true
  })

  const [menuOpen, setMenuOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const [isStarred, setIsStarred] = useState<boolean>(() => {
    try {
      const starred = JSON.parse(localStorage.getItem('goodday_starred_spaces') || '[]')
      return Array.isArray(starred) && starred.includes(spaceId)
    } catch {
      return false
    }
  })

  useEffect(() => {
    if (!spaceId) return
    try {
      const starred = JSON.parse(localStorage.getItem('goodday_starred_spaces') || '[]')
      setIsStarred(Array.isArray(starred) && starred.includes(spaceId))
    } catch {}
  }, [spaceId])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  const handleToggleStar = () => {
    if (!spaceId) return
    try {
      const starred: string[] = JSON.parse(localStorage.getItem('goodday_starred_spaces') || '[]')
      let next: string[]
      if (starred.includes(spaceId)) {
        next = starred.filter((id) => id !== spaceId)
        setIsStarred(false)
        toast.success('Removed space from starred')
      } else {
        next = [...starred, spaceId]
        setIsStarred(true)
        toast.success('Space starred')
      }
      localStorage.setItem('goodday_starred_spaces', JSON.stringify(next))
    } catch (e) {
      console.error('Failed to toggle star', e)
    }
    setMenuOpen(false)
  }

  const handleDeleteSpace = async () => {
    if (!spaceId) return
    setIsDeleting(true)
    try {
      await deleteSpace(spaceId)
      toast.success('Space deleted successfully')
      setShowDeleteConfirm(false)
      setMenuOpen(false)
      navigate('/spaces')
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete space'
      toast.error(msg)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="border-b border-gray-200/90 bg-white/95 dark:border-slate-800/90 dark:bg-slate-900/95 backdrop-blur-md flex-shrink-0 shadow-2xs">
      {/* Top Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 px-4 md:px-6 pt-3 pb-2.5">
        <div className="flex items-center gap-3 min-w-0">
          {onOpenMobileNav && (
            <button
              onClick={onOpenMobileNav}
              className="md:hidden -ml-1 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100 transition-colors"
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => navigate('/spaces')}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100 transition-colors"
            aria-label="Back to spaces"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-2.5">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg text-white shadow-md shadow-indigo-500/20 ring-1 ring-black/5 dark:ring-white/10"
              style={{ background: space?.color ?? 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
            >
              <span className="text-xs font-bold tracking-wider">{space?.key ? space.key.slice(0, 3).toUpperCase() : 'SP'}</span>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Spaces</div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold text-gray-900 dark:text-slate-100 tracking-tight">
                  {space?.name && space.name !== '…' ? space.name : (space ? 'Space' : 'Loading space…')}
                </h1>
                <button
                  type="button"
                  onClick={handleToggleStar}
                  className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
                    isStarred
                      ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/40 shadow-2xs'
                      : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:text-slate-500 dark:hover:text-amber-400 dark:hover:bg-amber-950/40 border border-gray-200 dark:border-slate-700'
                  }`}
                  title={isStarred ? 'Starred (click to unstar)' : 'Click to star this space'}
                  aria-label={isStarred ? 'Unstar space' : 'Star space'}
                >
                  <Star
                    className={`h-3.5 w-3.5 ${isStarred ? 'fill-amber-400 text-amber-500 dark:fill-amber-500 dark:text-amber-400' : 'text-gray-400 dark:text-slate-500'}`}
                  />
                </button>
                <button
                  onClick={onOpenMembers}
                  className="flex items-center gap-1.5 rounded-full bg-blue-50/80 px-2.5 py-0.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 dark:bg-blue-950/60 dark:text-blue-300 dark:hover:bg-blue-900/60 border border-blue-200/50 dark:border-blue-900/40 transition-colors cursor-pointer shadow-2xs"
                >
                  <Users className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                  <span>Members{space ? ` (${assignedCount !== undefined ? assignedCount : space.members.length})` : ''}</span>
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setMenuOpen((prev) => !prev)}
                    className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-800 transition-colors"
                    title="More actions"
                    aria-expanded={menuOpen}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>

                  {menuOpen && (
                    <div
                      ref={menuRef}
                      className="absolute left-0 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-800 dark:bg-slate-900 z-50 animate-in fade-in zoom-in-95 duration-100"
                    >
                      <button
                        type="button"
                        onClick={handleToggleStar}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-800 transition-colors"
                      >
                        <Star
                          className={`h-4 w-4 ${isStarred ? 'fill-amber-400 text-amber-500' : 'text-gray-400 dark:text-slate-500'}`}
                        />
                        <span>{isStarred ? 'Remove from starred' : 'Star this space'}</span>
                      </button>

                      <div className="my-1 border-t border-gray-100 dark:border-slate-800" />

                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false)
                          setShowDeleteConfirm(true)
                        }}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Delete space</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delete confirmation dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs animate-in fade-in duration-100">
            <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-slate-800 dark:bg-slate-900">
              <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100">Delete this space?</h3>
              <p className="mt-2 text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
                Are you sure you want to delete <strong className="text-gray-800 dark:text-slate-200">{space?.name || 'this space'}</strong>? This action will archive the space and its issues.
              </p>
              <div className="mt-5 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => setShowDeleteConfirm(false)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleDeleteSpace}
                  className="rounded-lg bg-rose-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50 transition-colors shadow-xs"
                >
                  {isDeleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          {actions}
        </div>
      </div>

      {/* Tab Navigation Bar with Vibrant Colors & Micro-badges */}
      {spaceId ? (
        <div className="relative border-t border-gray-100 dark:border-slate-800/80 bg-gradient-to-b from-gray-50/40 to-white dark:from-slate-900/60 dark:to-slate-900">
          <div className="flex items-center gap-1 overflow-x-auto px-4 md:px-6 pt-1.5 pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon
              const { theme } = tab
              return (
                <NavLink
                  key={tab.label}
                  to={`/spaces/${spaceId}${tab.suffix}`}
                  end
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2 px-3 py-2 text-xs rounded-t-lg transition-all duration-150 shrink-0 select-none outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                      isActive
                        ? cn(theme.activeBg, theme.activeText, 'font-semibold shadow-2xs')
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-800/60 font-medium',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* Icon container with colorful micro-badge */}
                      <span
                        className={cn(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-all duration-200',
                          isActive
                            ? cn(theme.activeIconBg, 'shadow-xs scale-105')
                            : cn(theme.iconBg, 'group-hover:scale-110 shadow-2xs'),
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-3.5 w-3.5 transition-colors',
                            isActive ? theme.activeIconColor : theme.iconColor,
                          )}
                        />
                      </span>

                      {/* Tab label */}
                      <span className="tracking-tight">{tab.label}</span>

                      {/* Active illuminated bottom line indicator */}
                      {isActive && (
                        <span
                          className={cn(
                            'absolute bottom-0 inset-x-2 h-[2.5px] rounded-full shadow-xs',
                            theme.activeIndicator,
                          )}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}
